import { log } from 'evlog/client';
import { Home } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { redirect, useLoaderData, useNavigate } from 'react-router';
import { toast } from 'sonner';

import { formatReservationDateTime } from '@virtual-care/isomorphic';
import {
  Button,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@virtual-care/shadcn';
import {
  apiErrorToMessage,
  resolveErrorMessage,
  useAction,
} from '@virtual-care/ui';

import { requiresAuth } from '../../auth-loader.ts';
import { DetailPill } from '../../components/booking/payment-card';
import Header from '../../components/header/header.tsx';
import ProfileCard from '../../components/header/profile-card.tsx';
import { homecareReservationStore } from '../../hooks/reservation/homecare.ts';
import { useBookingDetails } from '../../hooks/use-booking-details.ts';
import { toSar } from '../../hooks/use-payment-info.ts';

function PaymentDetailsCard({
  serviceName,
  price,
}: {
  serviceName: string;
  price: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border bg-white p-6">
      <h3 className="mb-4 text-lg font-bold text-gray-900">
        {t('homecare.confirmation.paymentDetailsHeader')}
      </h3>
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">{serviceName}</span>
          <span className="font-medium text-gray-900">
            {toSar(price * 100)}
          </span>
        </div>
        <div className="border-t border-gray-200 pt-3">
          <div className="flex items-center justify-between">
            <span className="font-bold text-gray-900">
              {t('homecare.requiredAmount')}
            </span>
            <span className="font-bold text-green-600">
              {toSar(price * 100)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function HomecareBookingConfirmation() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const data = useLoaderData() as Awaited<
    ReturnType<typeof homecareBookingConfirmationLoader>
  >;
  const isB2B = data.identity.userType === 'B2B';

  const hasOnBehalfInfo = !!data.reservation.patientName;

  const bookingDetails = useBookingDetails(
    data.identity,
    data.reservation.serviceName,
    hasOnBehalfInfo
      ? {
          patientName: data.reservation.patientName,
          patientBirthDate: data.reservation.patientBirthDate,
          patientGender: data.reservation.patientGender,
        }
      : undefined,
  );

  const createReservation = useAction('POST /homecare/reservations', {
    invalidate: ['GET /patients/me/homecare-reservations'],
  });

  const formattedDateTime = formatReservationDateTime({
    scheduledDate: data.reservation.scheduledDate,
    minutesFrom: data.reservation.scheduledMinutes,
    timezone: data.reservation.timezone,
  });

  const isProcessing = isB2B && createReservation.isPending;

  const handleConfirm = async () => {
    log.info({ action: 'homecare_confirm', isB2B });
    if (!isB2B) {
      return navigate('/homecare/payments');
    }

    const {
      vendorServiceId,
      description,
      scheduledDate,
      scheduledMinutes,
      timezone,
      files,
      patientName,
      patientBirthDate,
      patientGender,
    } = data.reservation;

    try {
      await createReservation.mutateAsync({
        vendorServiceId,
        description,
        scheduledDate,
        scheduledMinutes,
        timezone,
        ...(files ? { files } : {}),
        ...(patientName ? { patientName } : {}),
        ...(patientBirthDate ? { patientBirthDate } : {}),
        ...(patientGender === 'male' || patientGender === 'female'
          ? { patientGender }
          : {}),
      });

      navigate('/homecare/request-status?ok=1');
    } catch (error) {
      log.error({ action: 'homecare_confirm', error });
      toast.error(resolveErrorMessage(apiErrorToMessage(error)));
    }
  };

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="bg-chart-3 rounded-full p-2">
              <Home className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('homecare.pageTitle')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>
      <div className="card-container">
        <Card className="card">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-bold">
              {t('homecare.confirmation.title')}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t('homecare.confirmation.subtitle')}
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            <div className="rounded-xl border bg-white p-6">
              <h3 className="mb-4 text-lg font-bold text-gray-900">
                {t('homecare.confirmation.detailsHeader')}
              </h3>
              <div className="flex flex-wrap gap-3">
                {Object.entries(bookingDetails).map(([key, value]) => (
                  <DetailPill key={key} label={key} value={value} />
                ))}
                <DetailPill
                  label={t('homecare.provider')}
                  value={data.reservation.vendorName}
                />
                <DetailPill
                  label={t('homecare.appointment')}
                  value={formattedDateTime}
                />
              </div>
            </div>

            {!isB2B && (
              <PaymentDetailsCard
                serviceName={data.reservation.serviceName}
                price={parseFloat(data.reservation.servicePrice)}
              />
            )}
          </CardContent>
          <CardFooter className="flex w-full justify-center gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="secondary-button"
            >
              {t('homecare.back')}
            </Button>
            <Button
              variant="default"
              onClick={handleConfirm}
              disabled={isProcessing}
              className="primary-button bg-green-600 hover:bg-green-700"
            >
              {isProcessing
                ? t('homecare.confirmation.processing')
                : isB2B
                  ? t('homecare.confirmation.confirm')
                  : t('homecare.confirmation.payAndConfirm')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </>
  );
}

export async function homecareBookingConfirmationLoader() {
  const identity = await requiresAuth();
  const reservation = homecareReservationStore.read();
  if (!reservation) {
    throw redirect('/homecare');
  }
  return { identity, reservation };
}
